import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import clsx from "clsx";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles } from "@material-ui/core/styles";
import { Badge, Tooltip } from "@material-ui/core";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import { isQrCodeConnectionNeedingAttention } from "../utils/connections";

const useStyles = makeStyles(() => ({
  collapsedItem: {
    justifyContent: "center !important",
    padding: "8px 0 !important",
  },
  collapsedIcon: {
    minWidth: "0 !important",
    margin: "0 !important",
    display: "flex",
    justifyContent: "center",
  },
  collapsedText: {
    display: "none",
  },
}));

function ListItemLink(props) {
  const { icon, primary, to, className, collapsed } = props;
  const classes = useStyles();

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      <ListItem
        button
        component={renderLink}
        className={clsx(className, collapsed && classes.collapsedItem)}
      >
        {icon ? (
          <ListItemIcon className={clsx(collapsed && classes.collapsedIcon)}>
            {icon}
          </ListItemIcon>
        ) : null}
        <ListItemText
          primary={primary}
          className={clsx(collapsed && classes.collapsedText)}
        />
      </ListItem>
    </li>
  );
}

const MainListItems = (props) => {
  const { drawerClose, collapsed } = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const isAdmin = String(user?.profile || "").toUpperCase() === "ADMIN";

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setConnectionWarning((whatsApps || []).some(isQrCodeConnectionNeedingAttention));
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  return (
    <div onClick={drawerClose}>
      {isAdmin && (
        <>
          <ListItemLink
            to="/"
            primary={i18n.t("mainDrawer.listItems.indicators")}
            icon={<DashboardOutlinedIcon />}
            collapsed={collapsed}
          />
        </>
      )}
      <ListItemLink
        to="/tickets"
        primary={i18n.t("mainDrawer.listItems.tickets")}
        icon={<WhatsAppIcon />}
        collapsed={collapsed}
      />

      <ListItemLink
        to="/contacts"
        primary={i18n.t("mainDrawer.listItems.contacts")}
        icon={<ContactPhoneOutlinedIcon />}
        collapsed={collapsed}
      />
      <ListItemLink
        to="/quickAnswers"
        primary={i18n.t("mainDrawer.listItems.quickAnswers")}
        icon={<QuestionAnswerOutlinedIcon />}
        collapsed={collapsed}
      />
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <ListItemLink
              to="/users"
              primary={i18n.t("mainDrawer.listItems.users")}
              icon={<PeopleAltOutlinedIcon />}
              collapsed={collapsed}
            />
            <ListItemLink
              to="/queues"
              primary={i18n.t("mainDrawer.listItems.queues")}
              icon={<AccountTreeOutlinedIcon />}
              collapsed={collapsed}
            />
            <ListItemLink
              to="/settings"
              primary={i18n.t("mainDrawer.listItems.settings")}
              icon={<SettingsOutlinedIcon />}
              collapsed={collapsed}
            />
            <ListItemLink
              to="/connections"
              primary={i18n.t("mainDrawer.listItems.connections")}
              collapsed={collapsed}
              icon={
                <Tooltip
                  title={
                    connectionWarning
                      ? i18n.t("mainDrawer.connectionWarning")
                      : ""
                  }
                  placement="right"
                >
                  <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
                    <SyncAltIcon />
                  </Badge>
                </Tooltip>
              }
            />
          </>
        )}
      />
    </div>
  );
};

export default MainListItems;
